import { useQuery } from '@tanstack/react-query'

import { AudiencesApi } from 'api'

export interface EndUser {
  name?: string
  email?: string
  avatarUrl?: string
  id: number
  status?: string
}

export interface Company {
  about?: string
  domain?: string
  location?: string
  logo?: string
  name?: string
  noData?: boolean
  source?: string
  url?: string
}

export interface EndUserMeta extends EndUser {
  account: any
  bulkIndex: boolean
  company?: Company
  firstUpdate: boolean
  id: number
  location: Location
  orgId: number
  segments: any[]
  updatedAt: number
  attributes: Record<string, any>
  personalData: Record<string, any>
  companyData: Record<string, any>
  endUser?: EndUserMeta
}

export interface EndUserMetaInfo {
  resultSetSize: number
  visitors: EndUserMeta[]
  err: any
}

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

interface RequestPayload {
  requestSize: number
  hasLiveViewFromReach?: boolean
}

export const buildRequestPayload = ({
  requestSize,
  hasLiveViewFromReach = false
}: RequestPayload) => {
  const updatedAtInterval = hasLiveViewFromReach ? 'now-15m' : 'now-120s'
  const payload: any = {
    sorts: [{ property: 'session.activeSessionStartedAt', order: 'DESC' }],
    matchAll: [
      {
        property: 'updatedAt',
        value: updatedAtInterval,
        operation: 'GTE'
      },
      {
        property: 'session.activeSessionStartedAt',
        operation: 'HAS_PROPERTY'
      }
    ],
    match: [],
    matchAny: [],
    filterGroups: [],
    size: requestSize,
    timezone: userTimezone
  }

  const filters: Record<string, string>[] = []

  if (filters.length) {
    payload.filterGroups = [{ negateFilterGroup: false, shouldMatch: 1, filters }]
  }

  return payload
}

export async function fetchVisitors(query: any): Promise<any> {
  const params: Record<string, string> = {}

  if (query.timezone) {
    params.timezone = query.timezone
  }

  let queryString = new URLSearchParams(params).toString()

  if (queryString.length) {
    queryString = `?${queryString}`
  }

  try {
    const { data } = await AudiencesApi.post(`/visitor/searchWithCounts${queryString}`, query)

    return data
  } catch (err) {
    return { err }
  }
}

const REFRESH_INTERVAL = 1000 * 20 // 20 seconds;

export const useFetchVisitors = (payload: RequestPayload) => {
  return useQuery<EndUserMetaInfo, Error>({
    queryKey: ['fetchVisitors', payload.requestSize, payload.hasLiveViewFromReach],
    queryFn: () => fetchVisitors(buildRequestPayload(payload)),
    staleTime: REFRESH_INTERVAL
  })
}

export default useFetchVisitors
